import {
	formatDate,
	localOrUtcTimestampToLocalDate,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import {
	addMoneyFromDifferentCurrencies,
	convertMoneyToCurrency,
	currencyPairToHuman,
	formatMoneyByDecimal,
	makeCurrencyPair,
	moneyParseByDecimal,
	multipleMoneyBy,
	TFormattedMoney,
	TMoney,
} from "@sembark-travel/money"
import {
	Alert,
	Badge,
	Box,
	Button,
	Card,
	Col,
	Component,
	Divider,
	Dropdown,
	Grid,
	Icons,
	Inline,
	joinAttributes,
	Money,
	MoneySum,
	Select,
	Spinner,
	Stack,
	Table,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	CheckboxInputField,
	DatePickerField,
	FieldArray,
	Form,
	GetFieldValue,
	SelectField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { Fragment, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import { Email, PhoneNumber } from "../Contacts"
import {
	ISupplier,
	SelectSupplierContact,
	SelectVisaSuppliers,
} from "../Supplier"
import { FileInfo, FilePreview, SelectTourist, TTourist } from "../Tourists"
import { ITrip } from "../Trips"
import {
	getEntryTypeDisplayName,
	getPurposeOfVisaDisplayName,
	InfoStack,
	TVisaType,
	visaProcessingTimeStr,
	SelectedDocumentGroup,
} from "../Visa"
import {
	TAssignSupplierToBulkServiceType,
	TAddMoreVisaBookingFormValues,
	TAssignBulkGuestValues,
	TBookingOptionType,
	TBookingStage,
	TBookingState,
	TReinitiate,
	TVisaBooking,
	TVisaInBooking,
	TVisaServiceTypeEdit,
	XHR,
} from "./store"
import * as Validator from "yup"
import { DocumentFieldInput, IDocGroup } from "../DocGroups"
import { isNumeric } from "@sembark-travel/number-utils"
import { DocumentFileInputField } from "../DocTypes"
import { XHRLink } from "@sembark-travel/ui-router"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	ALL_STAGES,
	DROPPED_OPTIONS,
	getBookingPaidAmount,
	isBookingPaid,
	getBookingDueAmount,
	getBookingNetPayableAmount,
	getBookingRefundAmount,
	getAvailableStageOptions,
	isBookingPaymentSettled,
	REJECTED_OPTIONS,
	TRejectedReason,
} from "./utils"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import { TemporaryStorage } from "../TemporaryStorage"

import {
	CalculatePriceForm as CalculateVisaPrice,
	isVisaRateExpired,
} from "../VisaServicePrices"
import { INewQuoteState } from "../Trips/NewQuote"
import { IAccount, SelectAccount } from "../Accounting"

type TVisaBookingProps = {
	trip: ITrip
}

function areAllCommentsSame(bookings: TVisaBooking[]): boolean {
	if (bookings.length === 0) return true
	const firstComment = bookings[0]?.comments
	return bookings.every((b) => b.comments === firstComment)
}

type TTripVisaBookingsResponse = {
	data: TVisaInBooking[]
	meta: {
		can_modify: boolean
		can_view_prices: boolean
		is_shared: boolean
	}
}

export function VisaBookingsItem({ trip }: TVisaBookingProps) {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const { data, mutate: revalidate } = useSWR<TTripVisaBookingsResponse>(
		`/trip-visa-bookings/${trip.id}`,
		() => xhr.get(`/trip-visa-bookings/${trip.id}`).then((res) => res.data)
	)

	const visaServices = data?.data ?? []
	const canModify = data?.meta?.can_modify ?? false
	const canViewPrices = data?.meta?.can_view_prices ?? false

	function onSuccess() {
		revalidate()
	}
	const hasVisaServices = Array.isArray(visaServices) && visaServices.length > 0
	const canEdit = Boolean(canModify && !trip.is_locked)
	return (
		<Stack gap="4">
			<Inline justifyContent="between" flexWrap="wrap" gap="4">
				<Text fontWeight="semibold" fontSize="lg">
					Visa Bookings
				</Text>
				<Inline gap="2" flexWrap="wrap">
					{hasVisaServices && canEdit ? (
						<>
							<AssignSupplierToBulkServiceDialog
								visaServices={visaServices ?? []}
								trip={trip}
								onSuccess={onSuccess}
							>
								{({ open }) => (
									<Button
										status="primary"
										level="secondary"
										size="sm"
										onClick={open}
									>
										<Icons.Visa />
										Update Visa Supplier(s)
									</Button>
								)}
							</AssignSupplierToBulkServiceDialog>
							<AssignBulkGuestDialog
								visaServices={visaServices ?? []}
								trip={trip}
								onSuccess={() => {
									revalidate()
								}}
							>
								{({ open }) => (
									<Button
										status="primary"
										level="secondary"
										size="sm"
										onClick={open}
									>
										<Icons.User />
										Add Guest
									</Button>
								)}
							</AssignBulkGuestDialog>
						</>
					) : null}
					{hasPermission(PERMISSIONS.VIEW_VISA_BOOKINGS) ? (
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary" size="sm">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{hasVisaServices ? (
									<DownloadVisaBookings params={{ trip_id: trip.id }} />
								) : null}
								{canEdit ? (
									<>
										<AddMoreVisaBookings trip={trip} onSuccess={onSuccess} />
										<Dropdown.MenuItemDivider />
										<SyncBookingsWithQuote onSuccess={onSuccess} trip={trip} />
									</>
								) : null}
							</Dropdown.Menu>
						</Dropdown>
					) : null}
				</Inline>
			</Inline>
			{!hasVisaServices ? (
				<Text>Visa not provided.</Text>
			) : (
				<Table className="excel-style-table" responsive>
					<thead>
						<tr>
							<th>Visa</th>
							<th>Visa Type</th>
							<th>Status</th>
							<th>Supplier</th>
							{canViewPrices ? (
								<th style={{ textAlign: "right" }}>Price</th>
							) : null}
						</tr>
					</thead>
					<tbody>
						{(visaServices ?? []).map((service) => {
							const { id, name, visa_bookings } = service
							const isAllCommentsSame = areAllCommentsSame(visa_bookings)
							const comments = visa_bookings[0]?.comments
							const totalBookings = visa_bookings.length

							if (totalBookings === 0) {
								return (
									<Box as="tr" key={id}>
										<Box as="td">
											<VisaServiceDetailDialog
												service={service}
												trip={trip}
												onSuccess={onSuccess}
											>
												{({ open }) => (
													<Text
														color="accent"
														fontWeight="semibold"
														fontSize="base"
														cursor="pointer"
														onClick={open}
													>
														{name}
													</Text>
												)}
											</VisaServiceDetailDialog>
										</Box>
										<Box as="td" colSpan={4} color="muted">
											No visa bookings
										</Box>
									</Box>
								)
							}

							return visa_bookings.map((booking, i) => (
								<Box as="tr" key={booking.id}>
									{i === 0 ? (
										<Box as="td" rowSpan={totalBookings}>
											<Stack gap="1">
												<VisaServiceDetailDialog
													service={service}
													trip={trip}
													onSuccess={onSuccess}
												>
													{({ open }) => (
														<Text
															color="accent"
															fontWeight="semibold"
															fontSize="base"
															cursor="pointer"
															onClick={open}
														>
															{name}
														</Text>
													)}
												</VisaServiceDetailDialog>
												{isAllCommentsSame && comments ? (
													<Inline as="span" gap="1">
														<Box>
															<Icons.Annotation color="muted" />
														</Box>
														<Text as="span" fontSize="base" color="muted">
															{comments}
														</Text>
													</Inline>
												) : null}
											</Stack>
										</Box>
									) : null}
									<Box as="td">
										<BookingSummary
											booking={booking}
											trip={trip}
											service={service}
											onSuccess={onSuccess}
											canEdit={canEdit}
										/>
									</Box>
									<Box as="td">
										<LatestBookingStage
											booking={booking}
											onSuccess={onSuccess}
											canEdit={canEdit}
										/>
									</Box>
									<Box as="td">
										<SupplierInfo booking={booking} />
									</Box>
									{canViewPrices ? (
										<Box
											as="td"
											style={{ textAlign: "right", whiteSpace: "nowrap" }}
										>
											<PaymentSummary booking={booking} />
										</Box>
									) : null}
								</Box>
							))
						})}
					</tbody>
				</Table>
			)}
		</Stack>
	)
}

function SupplierInfo({ booking }: { booking: TVisaBooking }) {
	const { supplier, supplier_contact } = booking
	if (!supplier) return null
	return (
		<Stack gap="1" minWidth="max">
			<Inline gap="1" alignItems="center">
				<Text fontWeight="semibold" fontSize="base">
					{supplier.name}
				</Text>
				<Inline color="muted">
					{joinAttributes(
						supplier.contact?.phone_numbers ? (
							<Box>
								<PhoneNumber value={supplier.contact.phone_numbers} iconOnly />
							</Box>
						) : null,
						supplier.contact?.email ? (
							<Box>
								<Email value={supplier.contact?.email} iconOnly />
							</Box>
						) : null
					)}
				</Inline>
			</Inline>

			{supplier_contact ? (
				<Inline gap="1" alignItems="center" fontSize="sm" color="muted">
					<Text as="span">
						<Icons.ArrowLeft rotate="180" size="3" />{" "}
						{supplier_contact.name}{" "}
					</Text>
					<PhoneNumber value={supplier_contact.phone_numbers} iconOnly />
					{supplier_contact.email ? (
						<Email value={supplier_contact.email} iconOnly />
					) : null}
				</Inline>
			) : null}
		</Stack>
	)
}

function AssignBulkGuestDialog({
	children,
	onSuccess,
	visaServices,
	trip,
}: {
	trip: ITrip
	visaServices: TVisaInBooking[]
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
}) {
	const [isOpen, open, close] = useDialog()
	const formKeyRef = useRef(0)
	const xhr = useXHR()

	const allowedStates: Set<TBookingState> = new Set([
		"initialized",
		"document_pending",
		"document_received",
	])

	const initialValues: TAssignBulkGuestValues = {
		services: visaServices
			.filter(({ id }) => typeof id === "number")
			.map(({ id, name, visa_bookings }) => ({
				id: id as number,
				name: name ?? "",
				tripId: trip.id,
				visa_bookings: visa_bookings
					.filter((vb) => allowedStates.has(vb.latest_booking_stage.state))
					.map(({ id, visa_type, tourists }) => ({
						id,
						visa_type,
						tourist: tourists[0] ?? null,
					})),
			}))
			.filter((s) => s.visa_bookings.length > 0),
	}

	const handleOpen = () => {
		formKeyRef.current += 1
		open()
	}
	return (
		<>
			{children({ open: handleOpen })}
			<Dialog open={isOpen} onClose={close} md title="Add Guest">
				<Form<TAssignBulkGuestValues>
					key={formKeyRef.current}
					initialValues={initialValues}
					onSubmit={withServerErrors(async (values) => {
						const response = await XHR(xhr).bulkAssignGuest(trip.id, values)
						showSnackbar(response.message || `Guest assigned successfully`)
						onSuccess?.()
						close()
					})}
					subscription={{ submitting: true }}
					mutators={{ ...arrayMutators }}
				>
					{({ handleSubmit, submitting }) => (
						<form noValidate onSubmit={handleSubmit}>
							<FieldArray<
								TAssignBulkGuestValues["services"][number]
							> name="services">
								{({ fields: services }) => {
									if (!services || services.length === 0) {
										return (
											<Box paddingY="12">
												<Stack
													gap="3"
													alignItems="center"
													justifyContent="center"
												>
													<Icons.Info size="12" color="muted" />
													<Text fontWeight="semibold" fontSize="lg">
														No Services Available
													</Text>
													<Text
														color="muted"
														fontSize="base"
														textAlign="center"
													>
														There are no eligible visa booking services
														available to assign guests right now.
													</Text>
												</Stack>
											</Box>
										)
									}
									return (
										<>
											<Dialog.Body>
												<SubmissionError />
												<Table
													responsive
													borderWidth="1"
													borderColor="muted"
													striped
												>
													<Box as="thead" rounded="md">
														<Inline
															as="tr"
															borderBottomColor="muted"
															borderBottomWidth="1"
														>
															<Box
																as="th"
																paddingY="2"
																paddingX="4"
																style={{ width: "30%" }}
															>
																Service
															</Box>
															<Inline style={{ width: "70%" }}>
																<Box
																	as="th"
																	paddingY="2"
																	paddingX="4"
																	style={{ width: "60%" }}
																>
																	Service Type
																</Box>
																<Box
																	as="th"
																	paddingY="2"
																	paddingX="4"
																	style={{ width: "40%" }}
																>
																	Guests
																</Box>
															</Inline>
														</Inline>
													</Box>
													<Box as="tbody">
														{services.map((service) => (
															<SingleService name={service} key={service} />
														))}
													</Box>
												</Table>
											</Dialog.Body>
											<Dialog.Footer>
												<Inline gap="3" justifyContent="start" width="full">
													<Button
														type="submit"
														status="primary"
														disabled={services.length === 0 || submitting}
													>
														Update
													</Button>
													<Button
														type="button"
														onClick={close}
														disabled={submitting}
													>
														Cancel
													</Button>
												</Inline>
											</Dialog.Footer>
										</>
									)
								}}
							</FieldArray>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

const assignSupplierSchema = Validator.object({
	stage: Validator.mixed<"select_booking" | "assign_supplier">()
		.oneOf(["select_booking", "assign_supplier"])
		.optional(),
	supplier: Validator.mixed().required("Please select a service provider."),
	supplier_contact: Validator.mixed().optional().nullable(),
	check: Validator.boolean().optional(),
	services: Validator.array()
		.of(
			Validator.object({
				id: Validator.number().optional(),
				tripId: Validator.number().optional(),
				name: Validator.string().optional(),
				check: Validator.boolean().optional(),
				visa_bookings: Validator.array()
					.of(
						Validator.object({
							id: Validator.number().optional(),
							currency: Validator.string().optional(),
							visa_type: Validator.mixed<TVisaType>().optional(),
							booked_price: Validator.number().required(
								"Please enter the booked price for this booking."
							),
							tourist: Validator.mixed<TTourist>().optional().nullable(),
							check: Validator.boolean().optional(),
							remark: Validator.string().optional().nullable(),
						})
					)
					.required("At least one visa booking is required for each service."),
			})
		)
		.required("Please select at least one service to assign."),
})

function AssignSupplierToBulkServiceDialog({
	children,
	onSuccess,
	visaServices,
	trip,
}: {
	trip: ITrip
	visaServices: TVisaInBooking[]
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
}) {
	const [isOpen, open, close] = useDialog()
	const formKeyRef = useRef(0)
	const xhr = useXHR()
	const hideStatuses: Set<TBookingState> = new Set(["rejected", "dropped"])
	const initialValues: TAssignSupplierToBulkServiceType = {
		stage: "select_booking",
		supplier: visaServices[0].visa_bookings[0].supplier || undefined,
		supplier_contact:
			visaServices[0].visa_bookings[0].supplier_contact || undefined,
		check: false,
		services: visaServices
			.filter(({ id }) => typeof id === "number")
			.map(
				({
					id,
					name,
					currency,
					visa_bookings,
				}): TAssignSupplierToBulkServiceType["services"][number] => ({
					id: id as number,
					name: name ?? "",
					tripId: trip.id,
					check: false,
					visa_bookings: visa_bookings
						.filter((vb) => !hideStatuses.has(vb.latest_booking_stage.state))
						.map(
							({
								id,
								visa_type,
								tourists,
								country,
								given_price,
								booked_price,
								supplier,
								supplier_contact,
							}) => ({
								id,
								visa_type,
								country: country,
								check: false,
								remark: undefined,
								given_price: given_price,
								booked_price: Number(formatMoneyByDecimal(booked_price)),
								tourist: tourists[0] ?? null,
								currency,
								supplier,
								supplier_contact,
							})
						),
				})
			)
			.filter((s) => s.visa_bookings.length > 0),
	}

	const handleOpen = () => {
		formKeyRef.current += 1
		open()
	}

	return (
		<>
			{children({ open: handleOpen })}
			<Dialog
				open={isOpen}
				onClose={close}
				lg
				title="Assign Service Provider to Visa Bookings"
			>
				<Form<TAssignSupplierToBulkServiceType>
					key={formKeyRef.current}
					validate={validateFormValues(assignSupplierSchema)}
					initialValues={initialValues}
					onSubmit={withServerErrors(async (values) => {
						const response =
							await XHR(xhr).assignSupplierToBulkVisaBookings(values)
						showSnackbar(response.message || `Supplier assigned successfully`)
						onSuccess?.()
						close()
					})}
					subscription={{ submitting: true }}
					mutators={{ ...arrayMutators }}
				>
					{({ handleSubmit, submitting }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<SelectVisaBookings services={initialValues.services} />
								<AssignSupplier services={initialValues.services} />
								<SubmissionError />
							</Dialog.Body>
							<AssignSupplierFooter close={close} submitting={submitting} />
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function SingleService({ name }: { name: string }) {
	const { value: service } =
		useFieldValue<TAssignBulkGuestValues["services"][number]>(name)
	return (
		<Fragment key={service.id}>
			<Inline as="tr">
				<Box style={{ width: "30%" }} paddingY="2" paddingX="4">
					<Stack gap="1">
						<Text
							color="accent"
							fontWeight="semibold"
							fontSize="base"
							cursor="pointer"
						>
							{service.name}
						</Text>
					</Stack>
				</Box>
				<Stack
					style={{ width: "70%" }}
					flex="1"
					borderLeftWidth="1"
					borderLeftColor="muted"
				>
					<FieldArray<
						TAssignBulkGuestValues["services"][number]["visa_bookings"][number]
					>
						name={`${name}.visa_bookings`}
					>
						{({ fields: bookings }) =>
							bookings.map((booking, i) => (
								<Inline
									borderBottomWidth={i === bookings.length ? "0" : "1"}
									borderBottomColor={
										i === bookings.length ? "transparent" : "muted"
									}
									flex="1"
									key={booking}
								>
									<Box style={{ width: "60%" }} paddingY="2" paddingX="4">
										<Stack gap="1">
											<GetFieldValue<
												TAssignBulkGuestValues["services"][number]["visa_bookings"][number]["visa_type"]
											>
												name={`${booking}.visa_type`}
											>
												{({ value: visaType }) => (
													<Text fontWeight="semibold" fontSize="base">
														{visaType.name}
													</Text>
												)}
											</GetFieldValue>
										</Stack>
									</Box>
									<Box style={{ width: "40%" }} paddingY="2" paddingX="4">
										<SelectField
											name={`${booking}.tourist`}
											fetchOnMount
											select={SelectTourist}
											placeholder="Type to search..."
											tripId={service.tripId}
											cacheKey={`trip_${service.tripId}_visa_bookings_tourist`}
										/>
									</Box>
								</Inline>
							))
						}
					</FieldArray>
				</Stack>
			</Inline>
		</Fragment>
	)
}

type SelectVisaBookingsProps = {
	services: TAssignSupplierToBulkServiceType["services"]
}

function SelectVisaBookings({ services }: SelectVisaBookingsProps) {
	const form = useForm()
	const { value: stage } =
		useFieldValue<TAssignSupplierToBulkServiceType["stage"]>("stage")

	if (!services || services.length === 0) {
		return (
			<Box paddingY="12">
				<Stack gap="3" alignItems="center" justifyContent="center">
					<Icons.Info size="12" color="muted" />
					<Text fontWeight="semibold" fontSize="lg">
						No Services Found
					</Text>
					<Text color="muted" fontSize="base" textAlign="center">
						There are no visa booking services available to assign a provider.
					</Text>
				</Stack>
			</Box>
		)
	}

	if (stage === "assign_supplier") return null
	return (
		<Stack>
			<Stack gap="1">
				<Text fontWeight="semibold" fontSize="lg">
					Select Visa Booking
				</Text>
				<Text fontSize="base" color="muted">
					Please select applicable services which you want to assign a provider
					to.
				</Text>
			</Stack>
			<Divider />
			<Stack gap="4">
				<Inline
					gap="1"
					alignItems="center"
					padding="4"
					bgColor="subtle"
					borderWidth="1"
					borderColor="muted"
					rounded="md"
				>
					<CheckboxInputField
						name="check"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							const nextCheck = e.target.checked
							form.batch(() => {
								form.change("check", nextCheck)
								services.forEach((service, i) => {
									form.change(`services[${i}].check`, nextCheck)
									service.visa_bookings.forEach((_, j) => {
										form.change(
											`services[${i}].visa_bookings[${j}].check`,
											nextCheck
										)
									})
								})
							})
						}}
						label="Select All Services"
					/>
				</Inline>
				<FieldArray<TAssignBulkGuestValues["services"][number]> name="services">
					{({ fields: services }) =>
						services.map((service) => (
							<SingleServiceVisaBooking name={service} key={service} />
						))
					}
				</FieldArray>
			</Stack>
		</Stack>
	)
}

function SingleServiceVisaBooking({ name }: { name: string }) {
	const { value: service } =
		useFieldValue<TAssignSupplierToBulkServiceType["services"][number]>(name)
	const form = useForm()
	return (
		<Inline borderWidth="1" borderColor="muted" rounded="md">
			<Stack paddingY="2" paddingX="4" width="full" gap="2">
				<Inline
					gap="1"
					alignItems="center"
					cursor="pointer"
					onClick={() => {
						form.batch(() => {
							const serviceCheck = !service.check
							form.change(`${name}.check`, serviceCheck)
							service.visa_bookings.forEach((b, i) => {
								form.change(`${name}.visa_bookings[${i}].check`, serviceCheck)
							})
						})
					}}
				>
					<CheckboxInputField name={`${name}.check`} />
					<Text fontWeight="semibold">{service.name}</Text>
				</Inline>
				<Stack gap="2">
					<FieldArray<
						TAssignSupplierToBulkServiceType["services"][number]["visa_bookings"][number]
					>
						name={`${name}.visa_bookings`}
					>
						{({ fields: bookings }) =>
							bookings.map((booking, i) => (
								<GetFieldValue<
									TAssignSupplierToBulkServiceType["services"][number]["visa_bookings"][number]
								>
									name={booking}
									key={i}
								>
									{({ value: bookingValue }) => {
										return (
											<Inline gap="1" paddingLeft="3">
												<CheckboxInputField
													name={`${booking}.check`}
													onChange={(
														e: React.ChangeEvent<HTMLInputElement>
													) => {
														const nextCheck = e.target.checked
														form.batch(() => {
															form.change(`${booking}.check`, nextCheck)
															if (service.check && !nextCheck) {
																form.change(`${name}.check`, false)
															}
														})
													}}
													label={
														<Inline>
															{joinAttributes(
																<Text fontWeight="semibold" fontSize="base">
																	{bookingValue.visa_type.name}
																</Text>,
																bookingValue.tourist ? (
																	<Inline gap="1" alignItems="center">
																		<Icons.User size="4" color="muted" />
																		<Text fontWeight="normal">
																			{bookingValue.tourist.name}
																		</Text>
																	</Inline>
																) : null,
																bookingValue.supplier ? (
																	<Inline gap="1" alignItems="center">
																		<Icons.Building size="4" color="muted" />
																		<Text fontSize="sm">
																			{bookingValue.supplier.name}
																		</Text>
																		<Badge warning>Assigned</Badge>
																	</Inline>
																) : null
															)}
														</Inline>
													}
												/>
											</Inline>
										)
									}}
								</GetFieldValue>
							))
						}
					</FieldArray>
				</Stack>
			</Stack>
		</Inline>
	)
}

function SingleServiceInAssignSupplier({ name }: { name: string }) {
	const { value: service } =
		useFieldValue<TAssignSupplierToBulkServiceType["services"][number]>(name)
	const isAnyBookingChecked = service.visa_bookings.some((vb) => !!vb.check)
	const lastCheckedIndex = service.visa_bookings
		.map((vb, idx) => (vb.check ? idx : -1))
		.reduce((max, idx) => Math.max(max, idx), -1)
	if (!isAnyBookingChecked) return null
	return (
		<Stack
			borderWidth="1"
			borderColor="muted"
			rounded="lg"
			gap="2"
			width="full"
		>
			<Inline
				bgColor="subtle"
				roundedTop="lg"
				paddingX="4"
				paddingY="2"
				borderBottomWidth="1"
				borderBottomColor="muted"
			>
				<Text fontWeight="semibold" fontSize="lg">
					{service.name}
				</Text>
			</Inline>
			<Stack gap="2" paddingY="4">
				<FieldArray<
					TAssignSupplierToBulkServiceType["services"][number]["visa_bookings"][number]
				>
					name={`${name}.visa_bookings`}
				>
					{({ fields: bookings }) =>
						bookings.map((booking, i) => (
							<GetFieldValue<
								TAssignSupplierToBulkServiceType["services"][number]["visa_bookings"][number]
							>
								name={booking}
								key={i}
							>
								{({ value: bookingValue }) => {
									if (!bookingValue.check) return null
									return (
										<Stack paddingX="4">
											<Grid noGutters>
												<Col md={5} lg={5} xl={5}>
													<Inline gap="3">
														<Stack gap="3" paddingY="3" width="full">
															<Text fontWeight="semibold" fontSize="base">
																{bookingValue.visa_type.name}
															</Text>
															<TextAreaInputField
																name={`${booking}.remark`}
																label="Remarks for Supplier and Customer"
																secondaryLabel="optional"
																placeholder="Notes and remarks to be shared with supplier and customer..."
																rows={3}
															/>
														</Stack>
														<Box
															as="span"
															borderRightWidth="1"
															borderRightColor="muted"
															display={{ xs: "none", md: "inlineFlex" }}
														/>
													</Inline>
												</Col>

												<Col md={7} lg={7} xl={7}>
													<Inline
														height="full"
														alignItems="center"
														paddingLeft={{ xs: "0", md: "8" }}
													>
														<Table
															bordered
															responsive
															headers={[
																"Guest Name",
																"Given Price",
																"Booked Price",
															]}
															rows={[bookingValue].map((value) => [
																<Text fontSize="base" fontWeight="semibold">
																	{value.tourist?.name ?? "N/A"}
																</Text>,
																<GivenPrice booking={value} />,
																<VisaTypeBookedPrice
																	name={`${booking}.booked_price`}
																/>,
															])}
														/>
													</Inline>
												</Col>
											</Grid>
											{lastCheckedIndex === i ? null : <Divider marginY="6" />}
										</Stack>
									)
								}}
							</GetFieldValue>
						))
					}
				</FieldArray>
			</Stack>
		</Stack>
	)
}

type AssignSupplierProps = {
	services: TAssignSupplierToBulkServiceType["services"]
}

function AssignSupplier({ services }: AssignSupplierProps) {
	const form = useForm()
	const { value: stage } =
		useFieldValue<TAssignSupplierToBulkServiceType["stage"]>("stage")
	if (stage === "select_booking") return null
	const countryIds = Array.from(
		new Set(
			services
				.flatMap((s) => s.visa_bookings.map((vb) => vb.country?.id))
				.filter((id): id is number => id != null)
		)
	)

	return (
		<Stack>
			<Stack gap="3">
				<Stack gap="1">
					<Text fontWeight="semibold" fontSize="lg">
						Service Provider
					</Text>
					<Text fontSize="base" color="muted">
						Please provide the service provider and confirmation details for
						services.{" "}
					</Text>
				</Stack>
				<Stack>
					<Grid noGutters gap="4">
						<Col md={4}>
							<SelectField
								name={`supplier`}
								select={SelectVisaSuppliers}
								label="Service Provider"
								creatable
								visaCountries={countryIds}
								onChange={(value: ISupplier | undefined, _name) => {
									form.batch(() => {
										form.change(_name, value ?? null)
										form.change("supplier_contact", value?.contact ?? null)
									})
								}}
							/>
						</Col>
						<Col md={4}>
							<GetFieldValue<ISupplier | undefined> name="supplier">
								{({ value }) =>
									!value ? null : (
										<SelectField
											label="Point of Contact"
											fullWidth
											select={SelectSupplierContact}
											supplierId={value.id}
											name={`supplier_contact`}
											creatable
											placeholder="Select or Add new..."
										/>
									)
								}
							</GetFieldValue>
						</Col>
					</Grid>
					<Divider />
				</Stack>
			</Stack>
			<Stack gap="4">
				<Stack gap="1">
					<Text fontWeight="semibold" fontSize="lg">
						Preview Services
					</Text>
					<Text color="muted" fontSize="md">
						Please preview the selected services that will be assigned to this
						provider and set the available booking price.
					</Text>
				</Stack>
				<FieldArray<TAssignBulkGuestValues["services"][number]> name="services">
					{({ fields: services }) =>
						services.map((service) => (
							<SingleServiceInAssignSupplier name={service} key={service} />
						))
					}
				</FieldArray>
			</Stack>
		</Stack>
	)
}

function AssignSupplierFooter({
	submitting,
	close,
}: {
	submitting: boolean
	close: () => void
}) {
	const form = useForm<TAssignSupplierToBulkServiceType>()
	const { value: stage } =
		useFieldValue<TAssignSupplierToBulkServiceType["stage"]>("stage")
	const { value: services } =
		useFieldValue<TAssignSupplierToBulkServiceType["services"]>("services")
	if (stage === "select_booking") {
		return (
			<Dialog.Footer>
				<Inline gap="3" justifyContent="start" width="full">
					<Button
						type="button"
						level="primary"
						disabled={!services || services.length === 0}
						status="primary"
						onClick={() => {
							const checkedBookings = services
								.flatMap((s) => s.visa_bookings ?? [])
								.filter((b) => b.check)
							const bookingWithSupplier = checkedBookings.find(
								(b) => b.supplier
							)

							if (checkedBookings.length === 0) {
								showSnackbar(
									"Please select at least one service to assign a service provider."
								)
								return
							}
							form.change("supplier", bookingWithSupplier?.supplier)
							form.change(
								"supplier_contact",
								bookingWithSupplier?.supplier_contact
							)
							form.change("stage", "assign_supplier")
						}}
					>
						Next: Assign Provider <Icons.ChevronRight />
					</Button>
					<Button type="button" onClick={close} level="tertiary">
						Cancel
					</Button>
				</Inline>
			</Dialog.Footer>
		)
	}

	return (
		<Dialog.Footer>
			<Inline gap="3" justifyContent="between" width="full">
				<Button
					type="button"
					level="secondary"
					onClick={() => {
						form.change("stage", "select_booking")
					}}
				>
					<Icons.ChevronLeft /> Edit Services
				</Button>
				<Inline gap="2">
					<Button
						type="button"
						onClick={close}
						disabled={submitting}
						level="tertiary"
					>
						Cancel
					</Button>
					<Button type="submit" status="primary" disabled={submitting}>
						Assign
					</Button>
				</Inline>
			</Inline>
		</Dialog.Footer>
	)
}

function GivenPrice({
	booking,
}: Readonly<{
	booking: TAssignSupplierToBulkServiceType["services"][number]["visa_bookings"][number]
}>) {
	if (!booking?.given_price) return "N/A"
	return <Money money={booking.given_price} showCurrency />
}

function VisaTypeSystemRate({
	systemRate,
}: Readonly<{
	systemRate: TFormattedMoney
}>) {
	if (!systemRate) return "N/A"
	return <Money money={systemRate} showCurrency />
}

function VisaTypeBookedPrice({ name }: Readonly<{ name: string }>) {
	return (
		<Box>
			<TextInputField
				type="text"
				name={name}
				placeholder="0"
				style={{ maxWidth: "150px" }}
				min={1}
			/>
		</Box>
	)
}

export function VisaServiceDetailDialog({
	children,
	service,
	trip,
}: {
	trip: ITrip
	service: TVisaInBooking
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
}) {
	const [isOpen, open, close] = useDialog()
	const { name, visa_bookings } = service
	const allCommentsSame = areAllCommentsSame(visa_bookings)
	const comments = visa_bookings[0].comments
	const {
		id,
		tourist,
		no_of_adults,
		no_of_children,
		trip_source,
		trip_source_contact,
	} = trip
	const isAgent = trip_source.is_agent
	const sourceName = isAgent
		? trip_source_contact?.name
		: tourist
			? tourist.name
			: null
	const sourcePhoneNumbers =
		isAgent && trip_source_contact
			? trip_source_contact.phone_numbers
			: tourist
				? tourist?.phone_numbers
				: null
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} lg title="Visa Service">
				<Dialog.Body>
					<Stack gap="6">
						<Inline bgColor="subtle" padding="4" rounded="md">
							<Grid noGutters flexWrap="wrap" gap="4">
								<Col md={4}>
									<Stack style={{ width: "47%" }}>
										<Text fontWeight="semibold" fontSize="md">
											{name}
										</Text>
										{allCommentsSame && comments ? (
											<Inline as="span" gap="1">
												<Box>
													<Icons.Annotation color="muted" />
												</Box>
												<Text as="span" fontSize="base" color="muted">
													{comments}
												</Text>
											</Inline>
										) : null}
									</Stack>
								</Col>
								<Col>
									<Inline
										gap="6"
										justifyContent={{ xs: "start", md: "end" }}
										flexWrap="wrap"
									>
										<Stack gap="2">
											<Inline alignItems="center" minWidth="max">
												{joinAttributes(
													tourist ? (
														<Inline alignItems="center" gap="1">
															<Icons.User size="5" color="muted" />
															<Text fontWeight="semibold" fontSize="md">
																{tourist.name}
															</Text>
															<PhoneNumber
																iconOnly
																value={tourist.phone_number}
															/>
														</Inline>
													) : null,
													no_of_adults > 0 || no_of_children > 0 ? (
														<Text
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															{[
																no_of_adults > 0 ? `${no_of_adults}A` : null,
																no_of_children > 0
																	? `${no_of_children}C`
																	: null,
															]
																.filter(Boolean)
																.join(", ")}
														</Text>
													) : null
												)}
											</Inline>
											<Inline alignItems="center" paddingLeft="6">
												{joinAttributes(
													<Inline alignItems="center" gap="1">
														<Text fontSize="base" color="accent">
															Trip {id}
														</Text>
													</Inline>,
													sourceName ? (
														<Text fontSize="base" color="muted">
															{sourceName}
														</Text>
													) : null,
													sourcePhoneNumbers ? (
														<PhoneNumber iconOnly value={sourcePhoneNumbers} />
													) : null
												)}
											</Inline>
										</Stack>
										<Stack gap="2">
											<Text>Payment Status</Text>
											<BookingsStatusBadge bookings={visa_bookings} />
										</Stack>
										<Stack gap="2">
											<Text>Ops Team</Text>
											{trip.operations_team?.length ? (
												<Text textOverflow="truncate">
													{trip.operations_team
														.map((t, _, arr) =>
															arr.length > 1 ? t.name.slice(0, 4) : t.name
														)
														.join(", ")}
												</Text>
											) : (
												<Text>N/A</Text>
											)}
										</Stack>
									</Inline>
								</Col>
							</Grid>
						</Inline>

						<Stack gap="4">
							<Text fontSize="md" fontWeight="semibold">
								SERVICES TYPE
							</Text>
							<Stack gap="4">
								{visa_bookings.map((vb) => (
									<BookingInfo booking={vb} key={vb.id} />
								))}
							</Stack>
						</Stack>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function VisaTypeDetailDialog({
	children,
	onSuccess,
	service,
	trip,
	booking,
	canEdit,
}: {
	trip: ITrip
	service: TVisaInBooking
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
	booking: TVisaBooking
	canEdit: boolean
}) {
	const [isOpen, open, close] = useDialog()
	const { name, visa_bookings } = service
	const allCommentsSame = areAllCommentsSame(visa_bookings)
	const comments = visa_bookings[0].comments
	const {
		id,
		tourist,
		no_of_adults,
		no_of_children,
		trip_source,
		trip_source_contact,
	} = trip
	const isAgent = trip_source.is_agent
	const sourceName = isAgent
		? trip_source_contact?.name
		: tourist
			? tourist.name
			: null
	const sourcePhoneNumbers =
		isAgent && trip_source_contact
			? trip_source_contact.phone_numbers
			: tourist
				? tourist?.phone_numbers
				: null
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} lg title="Visa Service">
				<Dialog.Body>
					<Stack gap="6">
						<Inline bgColor="subtle" padding="4" rounded="md">
							<Grid noGutters flexWrap="wrap" gap="4">
								<Col md={4}>
									<Stack>
										<Text fontWeight="semibold" fontSize="md">
											{name}
										</Text>
										{allCommentsSame && comments ? (
											<Inline as="span" gap="1">
												<Box>
													<Icons.Annotation color="muted" />
												</Box>
												<Text as="span" fontSize="base" color="muted">
													{comments}
												</Text>
											</Inline>
										) : null}
									</Stack>
								</Col>
								<Col>
									<Inline
										gap="6"
										justifyContent={{ xs: "start", md: "end" }}
										flexWrap="wrap"
									>
										<Stack gap="2">
											<Inline alignItems="center" minWidth="max">
												{joinAttributes(
													tourist ? (
														<Inline alignItems="center" gap="1">
															<Icons.User size="5" color="muted" />
															<Text fontWeight="semibold" fontSize="md">
																{tourist.name}
															</Text>
															<PhoneNumber
																iconOnly
																value={tourist.phone_number}
															/>
														</Inline>
													) : null,
													no_of_adults > 0 || no_of_children > 0 ? (
														<Text
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															{[
																no_of_adults > 0 ? `${no_of_adults}A` : null,
																no_of_children > 0
																	? `${no_of_children}C`
																	: null,
															]
																.filter(Boolean)
																.join(", ")}
														</Text>
													) : null
												)}
											</Inline>
											<Inline alignItems="center" paddingLeft="6">
												{joinAttributes(
													<Inline alignItems="center" gap="1">
														<Text fontSize="base" color="accent">
															Trip {id}
														</Text>
													</Inline>,
													sourceName ? (
														<Text fontSize="base" color="muted">
															{sourceName}
														</Text>
													) : null,
													sourcePhoneNumbers ? (
														<PhoneNumber iconOnly value={sourcePhoneNumbers} />
													) : null
												)}
											</Inline>
										</Stack>
										<Stack gap="2">
											<Text>Payment Status</Text>
											<BookingStatusBadge booking={booking} />
										</Stack>
										<Stack gap="2">
											<Text>Ops Team</Text>
											{trip.operations_team?.length ? (
												<Text textOverflow="truncate">
													{trip.operations_team
														.map((t, _, arr) =>
															arr.length > 1 ? t.name.slice(0, 4) : t.name
														)
														.join(", ")}
												</Text>
											) : (
												<Text>N/A</Text>
											)}
										</Stack>
									</Inline>
								</Col>
							</Grid>
						</Inline>

						<Stack gap="4">
							<Text fontSize="md" fontWeight="semibold">
								SERVICES TYPE
							</Text>
							<Stack gap="4">
								<BookingInfo booking={booking} />
							</Stack>
						</Stack>
						{canEdit ? (
							<Stack gap="4">
								<Divider marginY="0" />
								<Inline gap="6">
									<VisaTypeEditDialog
										trip={trip}
										service={service}
										booking={booking}
										onSuccess={onSuccess}
									>
										{({ open }) => (
											<Button onClick={open}>
												<Icons.Pencil /> Edit
											</Button>
										)}
									</VisaTypeEditDialog>
								</Inline>
							</Stack>
						) : null}
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function BookingInfo({ booking }: { booking: TVisaBooking }) {
	const {
		id,
		visa_type,
		tourists,
		booked_price,
		supplier,
		supplier_contact,
		latest_booking_stage,
		comments,
		country,
	} = booking
	const { name } = visa_type
	const tourist = tourists[0]

	return (
		<Inline borderWidth="1" borderColor="muted" rounded="md" key={id}>
			<Stack style={{ width: "80%" }} paddingY="4" paddingLeft="4">
				<Stack gap="2">
					<Stack gap="1">
						<Inline gap="2">
							<Text fontSize="lg" fontWeight="semibold">
								{name}
							</Text>
							{tourist ? (
								<Badge>
									<Icons.User /> {tourist.name}
								</Badge>
							) : null}
						</Inline>
						<VisaTypeDisabled visaType={visa_type} />
						<CountryView country={country} />
						{comments ? (
							<Box borderLeftWidth="2" borderLeftColor="muted" paddingLeft="2">
								<Text color="muted" fontSize="base">
									(REMARKS) {comments}
								</Text>
							</Box>
						) : null}
					</Stack>
					{visa_type?.description ? (
						<Component initialState={false}>
							{({ state, setState }) => (
								<Stack gap="2">
									<Box>
										<Text
											color="primary"
											fontSize="base"
											fontWeight="semibold"
											cursor="pointer"
											onClick={() => {
												setState(!state)
											}}
										>
											Description{" "}
											{state ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
										</Text>
									</Box>
									{state ? <Markdown>{visa_type.description}</Markdown> : null}
								</Stack>
							)}
						</Component>
					) : null}
				</Stack>
				<Divider sm />
				<Inline gap="6">
					<Stack>
						<Text color="muted">STATUS</Text>
						<Text fontSize="md" fontWeight="semibold">
							{latest_booking_stage.title}
						</Text>
					</Stack>
					{supplier ? (
						<>
							<Stack>
								<Text color="muted">SERVICE PROVIDER</Text>
								<Inline gap="1">
									<Text fontWeight="semibold" fontSize="base">
										{supplier.name}
									</Text>
									<Inline color="muted">
										{joinAttributes(
											supplier.contact?.phone_numbers ? (
												<Box>
													<PhoneNumber
														value={supplier.contact.phone_numbers}
														iconOnly
													/>
												</Box>
											) : null,
											supplier.contact?.email ? (
												<Box>
													<Email value={supplier.contact?.email} iconOnly />
												</Box>
											) : null
										)}
									</Inline>
								</Inline>
							</Stack>
							{supplier_contact ? (
								<Stack>
									<Text color="muted">POINT OF CONTACT</Text>
									<Inline gap="1">
										<Text fontWeight="semibold" fontSize="base">
											{supplier_contact.name}
										</Text>
										<Inline color="muted">
											{joinAttributes(
												supplier_contact.phone_numbers ? (
													<Box>
														<PhoneNumber
															value={supplier_contact.phone_numbers}
															iconOnly
														/>
													</Box>
												) : null,
												supplier_contact.email ? (
													<Box>
														<Email value={supplier_contact.email} iconOnly />
													</Box>
												) : null
											)}
										</Inline>
									</Inline>
								</Stack>
							) : null}
						</>
					) : null}
				</Inline>
			</Stack>
			<Box
				paddingY="4"
				flex="1"
				borderLeftWidth="1"
				borderLeftColor="muted"
				paddingRight="4"
			>
				<Inline
					gap="1"
					fontWeight="semibold"
					fontSize="md"
					justifyContent="end"
				>
					<Text>
						Booking: <Money money={booked_price} showCurrency />
					</Text>
				</Inline>
			</Box>
		</Inline>
	)
}

function BookingSummary({
	booking,
	trip,
	service,
	onSuccess,
	canEdit,
}: {
	booking: TVisaBooking
	trip: ITrip
	service: TVisaInBooking
	onSuccess: () => void
	canEdit: boolean
}) {
	const { country, visa_type, comments, remarks } = booking
	const isAllCommentsSame = areAllCommentsSame(service.visa_bookings)
	return (
		<Stack gap="1">
			<VisaTypeDetailDialog
				service={service}
				trip={trip}
				booking={booking}
				onSuccess={onSuccess}
				canEdit={canEdit}
			>
				{({ open }) => (
					<Text
						color="accent"
						fontWeight="semibold"
						fontSize="base"
						cursor="pointer"
						as="a"
						href={`#visa-service-${service.id}`}
						onClick={(e) => {
							e.preventDefault()
							open()
						}}
					>
						{visa_type.name}
					</Text>
				)}
			</VisaTypeDetailDialog>
			<GuestInfo
				booking={booking}
				trip={trip}
				service={service}
				onSuccess={onSuccess}
				canEdit={canEdit}
			/>
			<CountryView country={country} />
			<VisaTypeDisabled visaType={visa_type} />
			{!isAllCommentsSame && comments ? (
				<Inline as="span" gap="1" maxWidth="xs">
					<Box>
						<Icons.Annotation color="muted" />
					</Box>
					<Text as="span" fontSize="base" color="muted">
						{comments}
					</Text>
				</Inline>
			) : null}
			{remarks ? (
				<Box
					borderLeftWidth="2"
					borderLeftColor="muted"
					paddingLeft="2"
					maxWidth="xs"
				>
					<Text color="muted" fontSize="base">
						{remarks}
					</Text>
				</Box>
			) : null}
		</Stack>
	)
}

export function GuestInfo({
	booking,
	trip,
	service,
	onSuccess,
	canEdit,
}: {
	booking: TVisaBooking
	trip: ITrip
	service: TVisaInBooking
	onSuccess: () => void
	canEdit: boolean
}) {
	const { tourists } = booking
	if (Array.isArray(tourists) && tourists[0]?.name) {
		return (
			<Box>
				<Badge size="base" accent>
					<Icons.UserRound /> {tourists[0].name}
				</Badge>
			</Box>
		)
	} else if (canEdit) {
		return (
			<Box>
				<AssignBulkGuestDialog
					visaServices={[{ ...service, visa_bookings: [booking] }]}
					trip={trip}
					onSuccess={onSuccess}
				>
					{({ open }) => (
						<Button status="warning" level="secondary" size="sm" onClick={open}>
							<Icons.UserPlus size="4" />
							No Guest Assigned
						</Button>
					)}
				</AssignBulkGuestDialog>
			</Box>
		)
	} else return null
}

export function LatestBookingStage({
	booking,
	onSuccess,
	minimal = false,
	canEdit: canEditBooking,
}: {
	booking: TVisaBooking
	onSuccess?: () => void
	minimal?: boolean
	canEdit: boolean
}) {
	const { hasPermission } = useCheckPermissions()
	const { latest_booking_stage, visa_media_url, can_reinitiate } = booking
	const {
		title,
		comments,
		state,
		created_by,
		created_at,
		reference_number,
		appointment_location,
		appointment_scheduled_at,
		rejection_reason,
		drop_reason,
	} = latest_booking_stage

	const editHiddenStates: TBookingState[] = [
		"initialized",
		"document_pending",
		"rejected",
		"dropped",
	]
	const canEdit = !editHiddenStates.includes(state) && canEditBooking
	const isAlreadyReinitiated = !can_reinitiate && state === "rejected"
	return (
		<Stack gap="1">
			<Inline gap="1" alignItems="center">
				<Inline
					fontWeight="semibold"
					fontSize="base"
					alignItems="center"
					minWidth="max"
				>
					{state === "dropped" ? <Icons.Ban color="warning" /> : null}
					{state === "approved" ? (
						<Icons.OkCircleSolid color="success" />
					) : null}
					<Text as="span">{title}</Text>
				</Inline>
				{isAlreadyReinitiated ? (
					<Badge status="primary">Already Reinitiated</Badge>
				) : null}
				{!minimal &&
				visa_media_url &&
				state === "approved" &&
				hasPermission(PERMISSIONS.VIEW_VISA_BOOKINGS) ? (
					<Tooltip content="Download Visa Document">
						<Button
							as={XHRLink}
							href={`/visa-bookings/${booking.id}/download`}
							download
							target="_blank"
							level="tertiary"
							size="sm"
							aria-label="Download Visa Document"
						>
							<Icons.DocumentDownload />
						</Button>
					</Tooltip>
				) : null}
				{canEdit ? (
					<UpdateVisaBookingStage bookingId={booking.id} onSuccess={onSuccess}>
						{({ open }) => <Icons.Pencil onClick={open} cursor="pointer" />}
					</UpdateVisaBookingStage>
				) : null}
			</Inline>
			{minimal ? null : (
				<>
					{state === "document_pending" ? (
						<Alert inline>
							<Text fontSize="sm">
								Please collect all the documents from the guest to proceed
								booking in next stages.
							</Text>
						</Alert>
					) : null}
					{reference_number && state === "approved" ? (
						<Box>
							<Badge>{reference_number}</Badge>
						</Box>
					) : null}
					{rejection_reason && state === "rejected" ? (
						<Text
							as="span"
							fontWeight="normal"
							fontSize="sm"
							maxWidth="xs"
							color="danger"
						>
							<Icons.CircleAlert /> {rejection_reason}
						</Text>
					) : null}
					{drop_reason && state === "dropped" ? (
						<Text
							as="span"
							fontWeight="normal"
							fontSize="sm"
							maxWidth="xs"
							color="warning"
						>
							<Icons.CircleAlert /> {drop_reason}
						</Text>
					) : null}
					{state === "appointment_scheduled" &&
					(appointment_scheduled_at || appointment_location) ? (
						<>
							{appointment_scheduled_at ? (
								<Text as="span" fontWeight="normal" fontSize="sm" maxWidth="xs">
									<Icons.Calendar color="muted" />{" "}
									{formatDate(
										appointment_scheduled_at.replace("Z", ""),
										"MMMM DD, YYYY [at] hh:mm A"
									)}
								</Text>
							) : null}
							{appointment_location ? (
								<Text as="span" fontWeight="normal" fontSize="sm" maxWidth="xs">
									<Icons.LocationMarker color="muted" />{" "}
									{appointment_location.name}
								</Text>
							) : null}
						</>
					) : null}
					{comments ? (
						<Text as="span" fontWeight="normal" fontSize="sm" maxWidth="xs">
							<Icons.Annotation color="muted" /> {comments}
						</Text>
					) : null}
					<Text fontSize="sm" color="muted">
						{joinAttributes(
							created_by ? `by ${created_by?.name}` : null,
							created_at ? utcTimestampToLocalDateTimeString(created_at) : null
						)}
					</Text>
				</>
			)}
			{canEditBooking ? (
				<VisaServiceBookingReinitiateDialog
					booking={booking}
					onSuccess={onSuccess}
				>
					{({ open }) => (
						<Box>
							<Button onClick={open} size="sm" fullWidth={false}>
								<Icons.Refresh /> Reinitiate
							</Button>
						</Box>
					)}
				</VisaServiceBookingReinitiateDialog>
			) : null}
		</Stack>
	)
}

export function SelectVisaBookingStatuses({
	latestBookingStage,
	...props
}: React.ComponentProps<typeof Select> & {
	latestBookingStage?: TVisaBooking["latest_booking_stage"]
}) {
	const options = getAvailableStageOptions(latestBookingStage)
	return <Select options={options} {...props} />
}

function getBookingStageSchema(booking: TVisaBooking) {
	const paymentSettled = isBookingPaymentSettled(booking)
	return Validator.object().shape({
		stage: Validator.mixed<TBookingOptionType>().required(
			"Please select a booking status to proceed."
		),
		appointment_location: Validator.mixed().when("stage", {
			is: (option: TBookingOptionType | undefined) =>
				option?.id === "appointment_scheduled",
			then: (schema) =>
				schema.required("Please provide the appointment location."),
			otherwise: (schema) => schema.nullable().optional(),
		}),
		supplier: Validator.mixed().when("stage", {
			is: (option: TBookingOptionType | undefined) =>
				option?.id === "document_verified" || option?.id === "dropped",
			then: (schema) => schema.nullable().optional(),
			otherwise: (schema) => schema.required("Supplier selection is required."),
		}),
		paid_amount: Validator.mixed().when("stage", {
			is: (option: TBookingOptionType | undefined) =>
				option?.id === "document_verified" ||
				option?.id === "dropped" ||
				paymentSettled,
			then: (schema) => schema.nullable().optional(),
			otherwise: (schema) =>
				schema.required("Please enter the paid amount to continue."),
		}),
		scheduled_date: Validator.string().when("stage", {
			is: (option: TBookingOptionType | undefined) =>
				option?.id === "appointment_scheduled",
			then: (schema) =>
				schema.required("Scheduled date is required for the appointment."),
			otherwise: (schema) => schema.nullable().optional(),
		}),
		comments: Validator.string().nullable().optional(),
	})
}

function getSupplierCreditAccounts(
	supplier?: ISupplier,
	currency?: string
): { accounts: IAccount[]; defaultAccount?: IAccount } {
	if (!supplier) return { accounts: [], defaultAccount: undefined }
	const existingAccounts = supplier.accounting_accounts || []
	if (existingAccounts.length > 0) {
		const accounts = existingAccounts.map((a) => ({
			...a,
			name: a.asset_type ? `${a.name} (${a.asset_type})` : a.name,
		}))
		const defaultAccount =
			existingAccounts.find((a) => a?.asset_type === currency) ||
			existingAccounts[0]
		return {
			accounts,
			defaultAccount: defaultAccount
				? {
						...defaultAccount,
						name: defaultAccount.asset_type
							? `${defaultAccount.name} (${defaultAccount.asset_type})`
							: defaultAccount.name,
					}
				: undefined,
		}
	}

	const fakeAccount: IAccount = {
		id: -1,
		name: supplier.name,
		is_public: false,
		can_disable: false,
		can_enable: false,
		can_edit: false,
		entity_type: "third_party",
		is_suspense: false,
		created_by: {} as never,
	}
	return {
		accounts: [fakeAccount],
		defaultAccount: fakeAccount,
	}
}

function UpdateVisaBookingStage({
	children,
	onSuccess,
	bookingId,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
	bookingId: number
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} md title="Update Booking Status">
				{isOpen ? (
					<UpdateVisaBookingStageForm
						close={close}
						bookingId={bookingId}
						onSuccess={onSuccess}
					/>
				) : null}
			</Dialog>
		</>
	)
}

interface UpdateVisaBookingStageFormProps {
	close: () => void
	bookingId: number
	onSuccess?: () => void
}

function UpdateVisaBookingStageForm({
	close,
	bookingId,
	onSuccess,
}: UpdateVisaBookingStageFormProps) {
	const xhr = useXHR()

	const {
		data: booking,
		error,
		mutate,
	} = useSWR<TVisaBooking>(
		`/visa-bookings/${bookingId}`,
		() =>
			xhr
				.get<{ data: TVisaBooking }>(`/visa-bookings/${bookingId}`)
				.then((res) => res.data.data),
		{
			revalidateIfStale: true,
			revalidateOnMount: true,
		}
	)

	if (error && !booking) {
		return (
			<Dialog.Body>
				<Alert status="error">{error.message}</Alert>
			</Dialog.Body>
		)
	}

	if (!booking) {
		return (
			<Dialog.Body>
				<Spinner padding="4" alignCenter />
			</Dialog.Body>
		)
	}

	return (
		<UpdateVisaBookingStageDialogContent
			close={close}
			booking={booking}
			onSuccess={() => {
				mutate()
				onSuccess?.()
			}}
		/>
	)
}

interface UpdateVisaBookingStageDialogContentProps {
	close: () => void
	booking: TVisaBooking
	onSuccess?: () => void
}

function UpdateVisaBookingStageDialogContent({
	close,
	booking,
	onSuccess,
}: UpdateVisaBookingStageDialogContentProps) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const {
		latest_booking_stage,
		supplier,
		supplier_contact,
		currency,
		visa_media_url,
		debitable_accounts,
	} = booking
	const {
		reference_number,
		comments,
		rejection_reason,
		drop_reason,
		appointment_scheduled_at,
		appointment_location,
	} = latest_booking_stage

	let processedRejectionReason: TRejectedReason | undefined = undefined
	let custom_rejection_reason: string | undefined = undefined
	if (rejection_reason) {
		const isOption = REJECTED_OPTIONS.includes(rejection_reason)
		if (isOption) {
			processedRejectionReason = rejection_reason as TRejectedReason
			custom_rejection_reason = undefined
		} else {
			processedRejectionReason = "Others"
			custom_rejection_reason = rejection_reason
		}
	}

	const functional_currency = user?.tenant?.functional_currency

	const defaultDebitAccount =
		debitable_accounts?.find((a) => a?.asset_type === currency) ||
		debitable_accounts?.[0]

	const { defaultAccount: defaultCreditAccount } = getSupplierCreditAccounts(
		supplier,
		currency
	)

	const defaultTxnCurrency =
		defaultDebitAccount?.asset_type || functional_currency || currency

	const due = getBookingDueAmount(booking)
	const dueAmount = due ? Number(formatMoneyByDecimal(due.amount)) : 0
	const bookedAmount = Number(formatMoneyByDecimal(booking.booked_price))
	const maxDueAmount = dueAmount || bookedAmount

	const initialValues = useMemo<TBookingStage>(() => {
		const stageOption =
			ALL_STAGES.find((o) => o.id === latest_booking_stage.state) ||
			getAvailableStageOptions(latest_booking_stage)[0]
		return {
			stage: stageOption,
			supplier: supplier,
			supplier_contact: supplier_contact,
			currency: currency,
			paid_amount: maxDueAmount,
			txn_currency: defaultTxnCurrency,
			currency_to_txn_currency_exchange_rate:
				currency === defaultTxnCurrency ? 1 : 0,
			txn_amount: currency === defaultTxnCurrency ? maxDueAmount : 0,
			debit_account: defaultDebitAccount
				? {
						...defaultDebitAccount,
						name: defaultDebitAccount.asset_type
							? `${defaultDebitAccount.name} (${defaultDebitAccount.asset_type})`
							: defaultDebitAccount.name,
					}
				: undefined,
			credit_account: defaultCreditAccount,
			reference_id: "",
			reference_number,
			url: visa_media_url,
			comments,
			rejection_reason: processedRejectionReason,
			custom_rejection_reason,
			drop_reason,
			appointment_location: appointment_location?.name || undefined,
			scheduled_date: appointment_scheduled_at?.replace("Z", ""),
		}
	}, [
		latest_booking_stage,
		supplier,
		supplier_contact,
		currency,
		maxDueAmount,
		defaultTxnCurrency,
		defaultDebitAccount,
		defaultCreditAccount,
		reference_number,
		visa_media_url,
		comments,
		processedRejectionReason,
		custom_rejection_reason,
		drop_reason,
		appointment_location,
		appointment_scheduled_at,
	])

	return (
		<Form<TBookingStage>
			initialValues={initialValues}
			validate={validateFormValues(getBookingStageSchema(booking))}
			onSubmit={withServerErrors(async (values) => {
				await XHR(xhr).updateVisaBookingStage(booking, values)
				showSnackbar(`Visa booking stage updated successfully`)
				onSuccess?.()
				close()
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Dialog.Body>
						<Stack gap="2">
							<Stack>
								<Stack gap="4">
									<Grid noGutters gap="4">
										<Col sm={6}>
											<SelectField
												label="Select Status"
												select={SelectVisaBookingStatuses}
												name="stage"
												fullWidth
												latestBookingStage={latest_booking_stage}
											/>
										</Col>
										<ScheduleDate />
										<ReferenceNumber />
										<SelectReason />
										<BookingLocation />
									</Grid>
									<SupplierInBookingStatus booking={booking} />
									<BookingPaymentFields booking={booking} />
									<AddVisaDocument />
								</Stack>
								<AnyComments />
							</Stack>
							<SubmissionError />
						</Stack>
					</Dialog.Body>
					<Dialog.Footer>
						<Inline gap="3" justifyContent="start" width="full">
							<Button type="submit" status="primary" disabled={submitting}>
								{submitting ? "Updating..." : "Update"}
							</Button>
							<Button type="button" onClick={close} disabled={submitting}>
								Cancel
							</Button>
						</Inline>
					</Dialog.Footer>
				</form>
			)}
		</Form>
	)
}

function VisaServiceBookingReinitiateDialog({
	children,
	onSuccess,
	booking,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
	booking: TVisaBooking
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	const { can_reinitiate } = booking
	if (!can_reinitiate) return null
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} md title="Reinitiate Booking">
				<Form<TReinitiate>
					initialValues={{
						additional_documents: [],
						comments: undefined,
					}}
					onSubmit={async (values) => {
						await XHR(xhr).reinitiateVisaBooking(booking.id, values)
						showSnackbar("Visa booking has been successfully reinitiated.")
						onSuccess?.()
						close()
					}}
					subscription={{ submitting: true }}
					mutators={{ ...arrayMutators }}
				>
					{({ handleSubmit, submitting }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<Alert status="info">
										<Stack gap="px">
											<Text
												color="primary"
												fontWeight="semibold"
												fontSize="base"
											>
												Select Documents Required for Reinitiation
											</Text>
											<Text fontSize="sm" style={{ textDecoration: "" }}>
												Select the missing or additional documents required for
												the new visa booking. These documents will be included
												in the booking request.
											</Text>
										</Stack>
									</Alert>
									<Box paddingTop="4">
										<DocumentFieldInput name={"additional_documents"} />
									</Box>
									<AnyComments />
									<Alert status="warning">
										<Text>
											<Text fontWeight="semibold" as="span">
												Note:
											</Text>{" "}
											Make sure to select all required documents for the new
											visa booking. You can add more documents if needed.
										</Text>
									</Alert>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Inline gap="3" justifyContent="start" width="full">
									<Button type="submit" status="primary" disabled={submitting}>
										{submitting ? "Updating..." : "Update"}
									</Button>
									<Button type="button" onClick={close} disabled={submitting}>
										Cancel
									</Button>
								</Inline>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

interface SupplierInBookingStatusProps {
	booking: TVisaBooking
}
function SupplierInBookingStatus({ booking }: SupplierInBookingStatusProps) {
	const form = useForm()
	const { value: stage } = useFieldValue<TBookingOptionType>("stage")
	const { value: currency } = useFieldValue<string>("currency")
	if (!stage) return null
	const optionalSupplierStatuses: TBookingState[] = ["dropped"]
	const serviceProviderLabelNote = optionalSupplierStatuses.includes(stage.id)
		? "optional"
		: ""
	return (
		<Grid noGutters gap="4">
			<Col sm={6}>
				<SelectField
					fullWidth
					label="Service Provider"
					secondaryLabel={serviceProviderLabelNote}
					name={`supplier`}
					select={SelectVisaSuppliers}
					visaCountries={booking.country ? [booking.country?.id] : []}
					onChange={(value: ISupplier | undefined, _name) => {
						const { defaultAccount } = getSupplierCreditAccounts(
							value,
							currency
						)
						form.batch(() => {
							form.change(_name as never, value as never)
							form.change("supplier_contact", value?.contact)
							form.change("credit_account", defaultAccount)
						})
					}}
				/>
			</Col>
			<Col sm={5}>
				<GetFieldValue<ISupplier | undefined> name="supplier">
					{({ value }) =>
						!value ? null : (
							<SelectField
								label="Point of Contact"
								fullWidth
								select={SelectSupplierContact}
								supplierId={value.id}
								name={`supplier_contact`}
								multiple={false}
								placeholder="Select..."
							/>
						)
					}
				</GetFieldValue>
			</Col>
		</Grid>
	)
}

function ScheduleDate() {
	const { value } = useFieldValue<TBookingOptionType>("stage")
	const showStatuses: TBookingState[] = ["appointment_scheduled"]
	if (!value?.id || !showStatuses.includes(value.id)) return null
	return (
		<Col xl={5}>
			<DatePickerField
				label="Scheduled Date"
				name="scheduled_date"
				dateFormat
				required
				timeFormat
			/>
		</Col>
	)
}

function BookingLocation() {
	const { value } = useFieldValue<TBookingOptionType>("stage")
	const showStatuses: TBookingState[] = ["appointment_scheduled"]
	if (!value?.id || !showStatuses.includes(value.id)) return null
	return (
		<Col sm={11}>
			<TextInputField
				label="Appointment Location"
				name="appointment_location"
				placeholder="e.g. US Embassy, Mumbai"
				required
			/>
		</Col>
	)
}

function AnyComments() {
	return (
		<Stack>
			<Divider marginY="6" />
			<Grid noGutters>
				<Col xl={11}>
					<TextInputField
						label="Any Comments"
						name="comments"
						secondaryLabel="optional"
						required
					/>
				</Col>
			</Grid>
		</Stack>
	)
}

function BookingPaymentFields({ booking }: { booking: TVisaBooking }) {
	const form = useForm()
	const { booked_price, debitable_accounts } = booking
	const { user } = useAuthUser()
	const { value: stage } = useFieldValue<TBookingOptionType>("stage")
	const { value: supplier } =
		useFieldValue<TVisaBooking["supplier"]>("supplier")

	const hideStatuses: TBookingState[] = ["document_verified"]
	const optionalStatuses: TBookingState[] = ["dropped"]
	const secondaryLabelNote =
		stage?.id && optionalStatuses.includes(stage.id) ? "optional" : ""

	const paid = getBookingPaidAmount(booking)
	const due = getBookingDueAmount(booking)
	const dueAmount = due ? Number(formatMoneyByDecimal(due.amount)) : 0
	const bookedAmount = Number(formatMoneyByDecimal(booked_price))
	const isAllPaymentSettled = isBookingPaymentSettled(booking)
	const maxDueAmount = dueAmount || bookedAmount

	const functional_currency = user?.tenant?.functional_currency

	if (!stage?.id || hideStatuses.includes(stage.id)) return null
	return (
		<>
			{!isAllPaymentSettled ? (
				<>
					<Grid gap="4">
						<Col>
							<GetFieldValue<IAccount> name="debit_account">
								{({ value: debit_account }) => (
									<SelectField
										label="Debit Account"
										select={SelectAccount}
										name="debit_account"
										disabled={isAllPaymentSettled}
										defaultOptions={
											(debitable_accounts || []).length
												? debitable_accounts.map((a) => ({
														...a,
														name: a.asset_type
															? `${a.name} (${a.asset_type})`
															: a.name,
													}))
												: undefined
										}
										onChange={(value: IAccount) => {
											form.change("debit_account", value)
											form.change(
												"txn_currency",
												value?.asset_type || functional_currency
											)
											if (
												(debit_account?.asset_type || functional_currency) !==
												value?.asset_type
											) {
												form.change("txn_amount", 0)
												form.change("currency_to_txn_currency_exchange_rate", 0)
											}
										}}
									/>
								)}
							</GetFieldValue>
						</Col>
						<Col>
							<SelectField
								label="Credit Account"
								select={SelectAccount}
								name="credit_account"
								disabled={isAllPaymentSettled}
								defaultOptions={(() => {
									const { accounts } = getSupplierCreditAccounts(
										supplier,
										booking.currency
									)
									return accounts.length ? accounts : undefined
								})()}
								onChange={(value: IAccount) => {
									form.change("credit_account", value)
								}}
							/>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="4">
						<Col xs={12} sm="auto">
							<GetFieldValue<string> name="currency">
								{({ value: currency }) => (
									<Inline gap="4" collapseBelow="md">
										<TextInputField
											label={
												<Tooltip
													content={
														isAllPaymentSettled
															? "All payments for this booking are already settled"
															: "Enter the paid amount for this visa booking"
													}
												>
													<Text>Paid Amount ({currency})</Text>
												</Tooltip>
											}
											secondaryLabel={secondaryLabelNote}
											name="paid_amount"
											type="number"
											min={0}
											max={maxDueAmount}
											disabled={isAllPaymentSettled}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												const rawValue = e.target.value
												if (rawValue === "") {
													form.change("paid_amount", "")
													return
												}
												const numericValue = Number(rawValue)
												if (Number.isNaN(numericValue)) {
													return
												}
												const finalValue = Math.max(
													0,
													Math.min(numericValue, maxDueAmount)
												)
												form.change("paid_amount", finalValue)
											}}
										/>
										<GetFieldValue<string> name={`txn_currency`}>
											{({ value: txn_currency }) =>
												txn_currency && currency !== txn_currency ? (
													<GetFieldValue<number> name="paid_amount">
														{({ value: paidAmount }) => (
															<Inline>
																<TextInputField
																	label={`Exchange (${currency}/${txn_currency})`}
																	name="currency_to_txn_currency_exchange_rate"
																	type="number"
																	placeholder="Exchange Rate"
																	min={0}
																	required
																	style={{ maxWidth: "150px" }}
																	disabled={isAllPaymentSettled}
																	help={({ value }) =>
																		value !== "" ? (
																			<Text>
																				{currencyPairToHuman(
																					makeCurrencyPair(
																						currency,
																						txn_currency || "INR",
																						Number(value) || 0
																					)
																				)}
																			</Text>
																		) : null
																	}
																	onChange={(e) => {
																		const value = e.currentTarget.value
																		form.change(
																			"currency_to_txn_currency_exchange_rate",
																			value as never
																		)
																		if (!value) {
																			form.change("txn_amount", 0)
																		} else {
																			form.change(
																				"txn_amount",
																				formatMoneyByDecimal(
																					convertMoneyToCurrency(
																						moneyParseByDecimal(
																							paidAmount || 0,
																							currency
																						),
																						txn_currency || "INR",
																						Number(value || 0) || 0
																					)
																				) as never
																			)
																		}
																	}}
																/>
																<Box paddingX="2" paddingTop="6">
																	<Icons.SwitchHorizontal />
																</Box>
																<TextInputField
																	label={`Amount (${txn_currency})`}
																	name="txn_amount"
																	type="number"
																	min={0}
																	required
																	placeholder="Amount"
																	style={{ maxWidth: "150px" }}
																	disabled={isAllPaymentSettled}
																	onChange={(e) => {
																		const value = e.currentTarget.value
																		form.change("txn_amount", value as never)
																		if (!value) {
																			form.change(
																				"currency_to_txn_currency_exchange_rate",
																				0
																			)
																		} else {
																			form.change(
																				"currency_to_txn_currency_exchange_rate",
																				Number(
																					Number(value || 0) /
																						Number(paidAmount || 1)
																				).toFixed(3) as never
																			)
																		}
																	}}
																/>
															</Inline>
														)}
													</GetFieldValue>
												) : null
											}
										</GetFieldValue>
									</Inline>
								)}
							</GetFieldValue>
						</Col>
					</Grid>
				</>
			) : null}
			<Inline fontSize="sm">
				{joinAttributes(
					booked_price ? (
						<Text>
							Booking: <Money money={booked_price} />
						</Text>
					) : null,
					paid ? (
						<Text color={paid.color}>
							Paid: <Money money={paid.amount} />
						</Text>
					) : null,
					due ? (
						<Text color="danger">
							Due Payable: <Money money={due.amount} />
						</Text>
					) : null
				)}
			</Inline>
			{!isAllPaymentSettled ? (
				<TextInputField
					name="reference_id"
					label="Reference Id"
					secondaryLabel="optional"
					type="text"
					placeholder="Reference Id of the payment"
				/>
			) : null}
		</>
	)
}

function ReferenceNumber() {
	const { value } = useFieldValue<TBookingOptionType>("stage")
	const showStatuses: TBookingState[] = ["approved"]
	if (!value?.id || !showStatuses.includes(value.id)) return null
	return (
		<Col sm={5}>
			<TextInputField
				label="Reference Number"
				name="reference_number"
				placeholder="Enter reference number"
				required
			/>
		</Col>
	)
}

function SelectReason() {
	const { value: bookingStatus } = useFieldValue<TBookingOptionType>("stage")
	const showStatuses: TBookingState[] = ["rejected", "dropped"]
	if (!bookingStatus?.id || !showStatuses.includes(bookingStatus.id))
		return null

	const options =
		bookingStatus.id === "rejected" ? REJECTED_OPTIONS : DROPPED_OPTIONS
	return (
		<>
			<Col sm={5}>
				<SelectField
					label="Select Reason"
					secondaryLabel={bookingStatus.id === "rejected" ? "optional" : ""}
					name={
						bookingStatus.id === "rejected" ? "rejection_reason" : "drop_reason"
					}
					fullWidth
					select={Select}
					placeholder="Type to search..."
					options={options}
				/>
			</Col>
			<GetFieldValue<TBookingStage["rejection_reason"]> name="rejection_reason">
				{({ value: reason }) =>
					reason === "Others" ? (
						<Col sm={12}>
							<TextInputField
								label="Rejection reason"
								name="custom_rejection_reason"
								placeholder="Provide detailed rejection reason"
								required
							/>
						</Col>
					) : null
				}
			</GetFieldValue>
		</>
	)
}

function AddVisaDocument() {
	const { value: stage } = useFieldValue<TBookingStage["stage"]>("stage")
	const { value: url } = useFieldValue<TBookingStage["url"]>("url")
	const { value: file } = useFieldValue<TBookingStage["file"]>("file")
	const showStatuses: TBookingState[] = ["approved"]
	const form = useForm<TBookingStage>()
	const inputRef = useRef<HTMLInputElement>(null)

	if (!stage?.id || !showStatuses.includes(stage.id)) return null

	async function triggerFileInput() {
		inputRef.current?.click()
		setTimeout(() => {
			const input = document.getElementById(
				`visa-file-input`
			) as HTMLInputElement | null
			if (input) input.click()
		}, 0)
	}

	return (
		<Grid noGutters>
			<Col xs={12}>
				{file || url ? (
					<Card bgColor="subtle" paddingX="4" paddingTop="2" paddingBottom="4">
						<Stack gap="2">
							<Inline paddingY="2" justifyContent="between" gap="2">
								<FileInfo file={file} url={url} />
								<Inline justifyContent="between" alignItems="center" gap="4">
									<Button
										level="secondary"
										status="primary"
										onClick={() => {
											form.change("file", undefined)
											form.change("url", undefined)
											triggerFileInput()
										}}
									>
										<Icons.Upload /> {file || url ? "Update" : "Upload"}
									</Button>
								</Inline>
							</Inline>
							<FilePreview url={url} file={file} />
						</Stack>
					</Card>
				) : (
					<DocumentFileInputField
						ref={inputRef}
						name="file"
						id={`visa-file-input`}
					/>
				)}
			</Col>
		</Grid>
	)
}

function VisaTypeEditDialog({
	children,
	onSuccess,
	booking,
	trip,
	service,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
	booking: TVisaBooking
	trip: ITrip
	service: TVisaInBooking
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()

	const { name } = service
	const {
		id,
		tourist,
		no_of_adults,
		no_of_children,
		trip_source,
		trip_source_contact,
	} = trip
	const isAgent = trip_source.is_agent
	const sourceName = isAgent
		? trip_source_contact?.name
		: tourist
			? tourist.name
			: null
	const sourcePhoneNumbers =
		isAgent && trip_source_contact
			? trip_source_contact.phone_numbers
			: tourist
				? tourist?.phone_numbers
				: null
	const { country } = booking
	const initialValues: TVisaServiceTypeEdit = {
		service,
		booking,
		booked_price: Number(formatMoneyByDecimal(booking.booked_price)),
		document_groups: booking.document_groups,
		supplier: booking.supplier,
		supplier_contact: booking.supplier_contact,
		remarks: booking.remarks ?? undefined,
	}

	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} xl title="Edit Visa Service Type">
				<Form<TVisaServiceTypeEdit>
					initialValues={initialValues}
					onSubmit={withServerErrors(async (values) => {
						await XHR(xhr).updateVisaTypeBooking(booking.id, values)
						showSnackbar(`Visa type booking updated successfully`)
						onSuccess?.()
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting, form }) => {
						return (
							<form noValidate onSubmit={handleSubmit}>
								<Dialog.Body>
									<Stack gap="6">
										<Inline bgColor="subtle" padding="4" rounded="md">
											<Grid noGutters flexWrap="wrap" gap="4">
												<Col md={4}>
													<Stack>
														<Text fontWeight="semibold" fontSize="md">
															{name}
														</Text>
														<CountryView country={country} />
													</Stack>
												</Col>
												<Col>
													<Inline
														gap="6"
														justifyContent={{ xs: "start", md: "end" }}
														flexWrap="wrap"
													>
														<Stack gap="2">
															<Inline alignItems="center">
																{joinAttributes(
																	tourist ? (
																		<Inline alignItems="center" gap="1">
																			<Icons.User size="5" color="muted" />
																			<Text fontWeight="semibold" fontSize="md">
																				{tourist.name}
																			</Text>
																			<PhoneNumber
																				iconOnly
																				value={tourist.phone_number}
																			/>
																		</Inline>
																	) : null,
																	no_of_adults > 0 || no_of_children > 0 ? (
																		<Text
																			fontWeight="semibold"
																			fontSize="sm"
																			color="muted"
																		>
																			{[
																				no_of_adults > 0
																					? `${no_of_adults}A`
																					: null,
																				no_of_children > 0
																					? `${no_of_children}C`
																					: null,
																			]
																				.filter(Boolean)
																				.join(", ")}
																		</Text>
																	) : null
																)}
															</Inline>
															<Inline alignItems="center" paddingLeft="6">
																{joinAttributes(
																	<Inline alignItems="center" gap="1">
																		<Text fontSize="base" color="accent">
																			Trip {id}
																		</Text>
																	</Inline>,
																	sourceName ? (
																		<Text fontSize="base" color="muted">
																			{sourceName}
																		</Text>
																	) : null,
																	sourcePhoneNumbers ? (
																		<PhoneNumber
																			iconOnly
																			value={sourcePhoneNumbers}
																		/>
																	) : null
																)}
															</Inline>
														</Stack>
														<Stack gap="2">
															<Text>Payment Status</Text>
															<BookingStatusBadge booking={booking} />
														</Stack>
														<Stack gap="2">
															<Text>Ops Team</Text>
															{trip.operations_team?.length ? (
																<Text textOverflow="truncate">
																	{trip.operations_team
																		.map((t, _, arr) =>
																			arr.length > 1
																				? t.name.slice(0, 4)
																				: t.name
																		)
																		.join(", ")}
																</Text>
															) : (
																<Text>N/A</Text>
															)}
														</Stack>
													</Inline>
												</Col>
											</Grid>
										</Inline>
										<Stack>
											<Stack gap="4">
												<Grid noGutters justifyContent="between">
													<Col md={7} lg={7} xl={7}>
														<Stack gap="4" width="full">
															<TextInputField
																name="booking.visa_type.name"
																label="Service Type"
																type="text"
																disabled
															/>
															<VisaTypeDisabled visaType={booking.visa_type} />
															<VisaTypeCardDetails booking={booking} />

															<GetFieldValue<IDocGroup[]>
																name={`document_groups`}
															>
																{({ value: documentGroup }) => {
																	return (
																		<SelectedDocumentGroup
																			label="Documents"
																			editMode
																			name={`document_groups[0]`}
																			documentGroup={documentGroup[0]}
																		/>
																	)
																}}
															</GetFieldValue>
														</Stack>
													</Col>
													<Col md={5} lg={5} xl={5}>
														<VisaTypePrices />
													</Col>
												</Grid>
												<Divider marginY="6" />
											</Stack>
											<Grid>
												<Col md={3}>
													<SelectField
														name={`supplier`}
														select={SelectVisaSuppliers}
														label="Service Provider"
														creatable
														visaCountries={
															booking.country ? [booking.country?.id] : []
														}
														onChange={(value: ISupplier | undefined, _name) => {
															form.batch(() => {
																form.change(_name as never, value as never)
																form.change("supplier_contact", value?.contact)
															})
														}}
													/>
												</Col>
												<Col md={3}>
													<GetFieldValue<ISupplier | undefined> name="supplier">
														{({ value }) =>
															!value ? null : (
																<SelectField
																	label="Point of Contact"
																	fullWidth
																	select={SelectSupplierContact}
																	supplierId={value.id}
																	name={`supplier_contact`}
																	creatable
																	multiple={false}
																	placeholder="Select or Add new..."
																/>
															)
														}
													</GetFieldValue>
												</Col>
												<Col md={6}>
													<TextAreaInputField
														name="remarks"
														label="Remarks for Customer"
														secondaryLabel="optional"
														placeholder="Notes and remarks to be shared with and customer..."
														rows={3}
													/>
												</Col>
											</Grid>
										</Stack>
										<SubmissionError />
									</Stack>
								</Dialog.Body>
								<Dialog.Footer>
									<Inline gap="3" justifyContent="start" width="full">
										<Button
											type="submit"
											status="primary"
											disabled={submitting}
										>
											Update
										</Button>
										<Button type="button" onClick={close} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
								</Dialog.Footer>
							</form>
						)
					}}
				</Form>
			</Dialog>
		</>
	)
}

function VisaTypeCardDetails({ booking }: { booking: TVisaBooking }) {
	const { visa_type } = booking
	const {
		duration_formatted,
		entry_type,
		purpose,
		processing_time_in_hours,
		apply_before_days,
	} = visa_type
	const processingTime = visaProcessingTimeStr(processing_time_in_hours)
	return (
		<Box
			borderWidth="1"
			borderColor="muted"
			bgColor="subtle"
			paddingY="4"
			paddingX="6"
			rounded="md"
		>
			<Inline gap="4">
				{entry_type ? (
					<InfoStack
						icon={<Icons.Users />}
						label="Entry Type"
						subtext={getEntryTypeDisplayName(entry_type)}
					/>
				) : null}
				{purpose ? (
					<InfoStack
						icon={<Icons.Users />}
						label="Purpose of Visa"
						subtext={getPurposeOfVisaDisplayName(purpose)}
					/>
				) : null}
				{duration_formatted ? (
					<InfoStack
						icon={<Icons.Calendar />}
						label="Duration"
						subtext={duration_formatted}
					/>
				) : null}
				{processingTime ? (
					<InfoStack
						icon={<Icons.Clock />}
						label="Processing Time"
						subtext={processingTime}
					/>
				) : null}
				{Number(apply_before_days) > 0 ? (
					<InfoStack
						icon={<Icons.CheckList />}
						label="Application Minimum Days"
						subtext={`${apply_before_days} Days`}
					/>
				) : null}
			</Inline>
		</Box>
	)
}

function VisaTypePrices() {
	const { value: booking } =
		useFieldValue<TVisaServiceTypeEdit["booking"]>("booking")
	const { tourists, system_price } = booking
	const tourist = tourists[0] ? tourists[0] : null
	return (
		<Inline height="full" paddingLeft="8">
			<Table
				bordered
				responsive
				headers={["Guest Name", "Rate", "Booked Price"]}
				rows={[
					[
						<Text fontSize="base" fontWeight="semibold">
							{tourist?.name ?? "N/A"}
						</Text>,
						<VisaTypeSystemRate systemRate={system_price} />,
						<VisaTypeBookedPrice name={"booked_price"} />,
					],
				]}
			/>
		</Inline>
	)
}

function PaymentSummary({ booking }: { booking: TVisaBooking }) {
	return (
		<Stack alignItems="end">
			<BookingAmount booking={booking} />
			<NetPayableAmount booking={booking} />
			<AmountPaid booking={booking} />
			<DuePayable booking={booking} />
			<RefundAmount booking={booking} />
		</Stack>
	)
}

function BookingStatusBadge({ booking }: { booking: TVisaBooking }) {
	const booked = isBookingPaid(booking)
	return (
		<Box>
			<Badge {...(booked ? { success: true } : { danger: true })}>
				{booked ? "Paid" : "Payment Due"}
			</Badge>
		</Box>
	)
}

function BookingsStatusBadge({ bookings }: { bookings: TVisaBooking[] }) {
	// Returns true if ALL bookings have payment completed (are "booked")
	// Uses isBookingPaid for each booking; expects a boolean for each.
	const allPaid = bookings.every((booking) => isBookingPaid(booking))
	return (
		<Box>
			<Badge {...(allPaid ? { success: true } : { danger: true })}>
				{allPaid ? "Paid" : "Payment Due"}
			</Badge>
		</Box>
	)
}

function PaymentBookedIcon({ booking }: { booking: TVisaBooking }) {
	const booked = isBookingPaid(booking)
	if (!booked) return null
	return (
		<Box display="inlineBlock" marginLeft="1">
			<Icons.OkCircleSolid color="success" />
		</Box>
	)
}

function BookingAmount({ booking }: { booking: TVisaBooking }) {
	const { booked_price } = booking
	if (!isNumeric(booked_price.amount)) return null
	return (
		<Inline as="span" gap="1" alignItems="center" justifyContent="end">
			<PaymentBookedIcon booking={booking} />
			<Text fontWeight="semibold">
				Booking: <Money money={booked_price} showCurrency />
			</Text>
		</Inline>
	)
}

function AmountPaid({ booking }: { booking: TVisaBooking }) {
	const paid = getBookingPaidAmount(booking)
	if (!paid) return null

	return (
		<Text fontWeight="semibold" fontSize="sm" color={paid.color} title="Paid">
			Amount Paid: <Money money={paid.amount} fontWeight="normal" />
		</Text>
	)
}

function DuePayable({ booking }: { booking: TVisaBooking }) {
	const due = getBookingDueAmount(booking)
	if (!due) return null

	return (
		<Box fontWeight="semibold" fontSize="sm" color="muted">
			Due Payable: <Money money={due.amount} showCurrency />
		</Box>
	)
}

function RefundAmount({ booking }: { booking: TVisaBooking }) {
	const refund = getBookingRefundAmount(booking)

	if (!refund) return null

	return (
		<Inline
			fontWeight="semibold"
			fontSize="sm"
			color="muted"
			alignItems="center"
			justifyContent="end"
			title="Refund"
		>
			Refund: <Money money={refund.paid_refund_amount} />
			{" / "}
			<Money money={refund.refund_amount} />
		</Inline>
	)
}

function NetPayableAmount({ booking }: { booking: TVisaBooking }) {
	const netPayable = getBookingNetPayableAmount(booking)
	if (!netPayable) return null

	return (
		<Box
			fontWeight="semibold"
			fontSize="sm"
			color="warning"
			title="Net Payable"
		>
			Net Payable: <Money money={netPayable.amount} />
		</Box>
	)
}

function CountryView({ country }: { country: TVisaBooking["country"] }) {
	if (!country) return null
	return (
		<Inline gap="1">
			<Icons.Globe />
			<Text as="span" fontSize="sm" color="muted">
				{country.name}
			</Text>
		</Inline>
	)
}

function AddMoreVisaBookings({
	trip,
	onSuccess,
}: {
	trip: ITrip
	onSuccess: () => void
}) {
	return (
		<AddMoreVisaBookingDialog trip={trip} onSuccess={onSuccess}>
			{({ open }) => (
				<Dropdown.MenuItem onClick={open}>
					<Icons.Plus /> Add More Bookings
				</Dropdown.MenuItem>
			)}
		</AddMoreVisaBookingDialog>
	)
}

function SyncBookingsWithQuote({
	onSuccess,
	trip,
}: {
	onSuccess: () => void
	trip: ITrip
}) {
	const xhr = useXHR()
	const [isUpdating, setIsUpdating] = useState(false)
	return (
		<Dropdown.MenuItem
			onClick={(e: React.MouseEvent) => {
				e.preventDefault()
				if (isUpdating) {
					return false
				}
				if (
					window.confirm(
						"Visa bookings will be updated using the latest Quote from this Trip.\n\nAre you sure you want to continue?"
					)
				) {
					setIsUpdating(true)
					xhr
						.post(`/trip-visa-bookings/${trip.id}/sync-with-latest-quote`)
						.then(() => {
							showSnackbar("Visa bookings have been synced with Latest Quote")
						})
						.catch((error) => {
							const e = error as Error
							alert(
								e.message || "Something went wrong. Please try after sometime."
							)
						})
						.then(() => {
							// always revalidate
							onSuccess()
							setIsUpdating(false)
						})
				}
			}}
		>
			<Icons.Refresh spin={isUpdating} /> Sync Bookings with Quote
		</Dropdown.MenuItem>
	)
}

export function DownloadVisaBookings({
	params = {},
}: {
	params?: Record<string, unknown>
}) {
	return (
		<TemporaryStorage initUrl={"/visa-bookings/download"} initParams={params}>
			{({ generate, url, message, reset }) => (
				<Dropdown.MenuItem
					onClick={!url ? generate : () => reset()}
					href={url || "#"}
					target={url ? "_blank" : undefined}
				>
					{message || (
						<>
							<Icons.DocumentDownload /> Download Services
						</>
					)}
				</Dropdown.MenuItem>
			)}
		</TemporaryStorage>
	)
}

function AddMoreVisaBookingDialog({
	children,
	onSuccess,
	trip,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
	trip: ITrip
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	const { destinations, no_of_adults, no_of_children } = trip
	const totalPax = no_of_adults + (no_of_children || 0)
	const startDate = formatDate(
		localOrUtcTimestampToLocalDate(trip.start_date_local, trip.start_date),
		"YYYY-MM-DD HH:mm:ss"
	)
	return (
		<>
			{children({ open })}
			<Dialog
				open={isOpen}
				onClose={close}
				fitContainer
				title="Add More Visa Bookings"
			>
				<Form<TAddMoreVisaBookingFormValues>
					initialValues={{
						visas: {
							services: [
								{
									country: undefined,
									visa: undefined,
									visa_type: undefined,
									booked_price: 0,
									per_quantity_booked_price: 0,
									pax: totalPax,
									showComment: false,
								},
							],
						},
						visasPrice: [],
					}}
					onSubmit={withServerErrors(async (values) => {
						const response = await XHR(xhr).addMoreVisaBookings(
							trip.id,
							values,
							startDate
						)
						showSnackbar(
							response.message || `Visa bookings created successfully.`
						)
						onSuccess?.()
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting, form }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<CalculateVisaPrice
										initialData={{
											services: [
												{
													country: undefined,
													visa: undefined,
													visa_type: undefined,
													booked_price: 0,
													per_quantity_booked_price: 0,
													pax: totalPax,
													showComment: false,
												},
											],
										}}
										tripDestinations={destinations}
										totalPax={totalPax}
										isEditMode={false}
										startDate={startDate}
										onChange={(services) => {
											const servicesList = Array.isArray(services?.services)
												? services.services.filter(
														(s) => s.country && s.visa && s.visa_type && s.pax
													)
												: []
											const totalPrices: TMoney[] = []
											servicesList.forEach((vs) => {
												if (vs.booked_price && vs.visa?.currency) {
													totalPrices.push(
														moneyParseByDecimal(
															Number(vs.booked_price) || 0,
															vs.visa?.currency
														)
													)
												}
											})
											form.batch(() => {
												form.change("visas", {
													services: servicesList,
												})
												form.change(
													"visasPrice",
													addMoneyFromDifferentCurrencies(totalPrices)
												)
											})
										}}
										showBookedPrices
									/>
									<TotalVisasPrice startDate={startDate} />
									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Inline gap="3" justifyContent="start" width="full">
									<Button type="submit" status="primary" disabled={submitting}>
										Update
									</Button>
									<Button type="button" onClick={close} disabled={submitting}>
										Cancel
									</Button>
								</Inline>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function TotalVisasPrice({ startDate }: { startDate: string }) {
	const { value: visasPrice } =
		useFieldValue<INewQuoteState["visasPrice"]>("visasPrice")

	const { value: visas } = useFieldValue<INewQuoteState["visas"]>("visas")
	const visaCostPrice = Array.isArray(visas?.services)
		? visas.services
				.map((s) => {
					const isRateExpired = s.visa_type
						? isVisaRateExpired({
								rateValidityDate: s.visa_type.price_valid_till,
								startDate,
							})
						: false
					if (!s?.visa_type?.cost_price || !s.pax || isRateExpired) return null
					return multipleMoneyBy(s?.visa_type?.cost_price, s.pax)
				})
				.filter((price): price is TFormattedMoney => !!price)
		: []
	return (
		<Box as="footer" textAlign="right">
			<Box
				bgColor="warning"
				display="inlineBlock"
				paddingX="4"
				paddingY="2"
				fontWeight="semibold"
				borderWidth="1"
				borderColor="warning"
				color="warning"
				rounded="md"
			>
				<Stack gap="1">
					<Inline gap="1" justifyContent={"end"}>
						Visa Cost Total:
						<MoneySum money={addMoneyFromDifferentCurrencies(visaCostPrice)} />
					</Inline>

					<Inline gap="1" justifyContent={"end"}>
						Visa Booking Total:
						<MoneySum money={addMoneyFromDifferentCurrencies(visasPrice)} />
					</Inline>
				</Stack>
			</Box>
		</Box>
	)
}

export function VisaTypeDisabled({
	visaType,
}: {
	visaType?: TVisaType | null
}) {
	if (!visaType?.deleted_at) return null
	return (
		<Box>
			<Badge warning>
				<Icons.Ban /> Disabled
			</Badge>
		</Box>
	)
}
